/* eslint-disable prefer-arrow-callback */
import { type Dispatch, type MutableRefObject, type SetStateAction, useEffect } from "react";

import type { PostHydrationLatch } from "pages/app/scenario-analysis/hooks/usePostHydrationLatch";
import { type CropYear, type ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { decidePostHydrationAction } from "pages/app/scenario-analysis/utils/postHydrationAction";

type AutoRecalcAfterHydrationArgs = {
  soilSightProjectId: string | null;
  hydrationApplied: boolean;
  scenarios: ScenarioData[];
  cropYearsSavedRef: MutableRefObject<Map<string, string>>;
  isSavingCount: number;
  farmFieldsFetching: boolean;
  fieldReselectNeeded: boolean;
  pendingReselectRecalcFieldRef: MutableRefObject<string | null>;
  fieldImportAlreadyPersistedRef: MutableRefObject<boolean>;
  transitionImportReadyForRecalc: boolean;
  /** The page landed on the entry dialog and it is still open; flipping false re-runs the decision. */
  isAwaitingProjectChoice: boolean;
  importedFieldCropYearsRef: MutableRefObject<CropYear[] | null>;
  addToExistingFlowRef: MutableRefObject<boolean>;
  postHydrationLatch: PostHydrationLatch;
  /** Tells the field-import landing scroll that this recalculation is the one it waits for. */
  markLandingRecalcPending: () => void;
  pruneAndPersistEmptyYears: (scenarios: ScenarioData[]) => Promise<ScenarioData[] | null>;
  recalculate: () => Promise<void>;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  setAwaitingInitialCalc: Dispatch<SetStateAction<boolean>>;
  flagIncompleteScenarios: () => void;
  revealFirstIncompleteScenario: () => void;
};

/** Runs (or deliberately skips) the first calculation once a project has hydrated; the decision is decidePostHydrationAction. */
export const useAutoRecalcAfterHydration = ({
  soilSightProjectId,
  hydrationApplied,
  scenarios,
  cropYearsSavedRef,
  isSavingCount,
  farmFieldsFetching,
  fieldReselectNeeded,
  pendingReselectRecalcFieldRef,
  fieldImportAlreadyPersistedRef,
  transitionImportReadyForRecalc,
  isAwaitingProjectChoice,
  importedFieldCropYearsRef,
  addToExistingFlowRef,
  postHydrationLatch,
  markLandingRecalcPending,
  pruneAndPersistEmptyYears,
  recalculate,
  setIsDirty,
  setAwaitingInitialCalc,
  flagIncompleteScenarios,
  revealFirstIncompleteScenario,
}: AutoRecalcAfterHydrationArgs): void => {
  useEffect(
    function resetPostHydrationRecalcGuardOnProjectChange() {
      postHydrationLatch.reset();
    },
    [postHydrationLatch, soilSightProjectId]
  );

  useEffect(
    function autoRecalcAfterHydration() {
      const action = decidePostHydrationAction({
        hydrationApplied,
        hasProject: !!soilSightProjectId,
        isDecided: postHydrationLatch.isDecided(),
        scenarios,
        savedCropYears: cropYearsSavedRef.current,
        isSaving: isSavingCount > 0,
        isFieldListFetching: farmFieldsFetching,
        isFieldReselectNeeded: fieldReselectNeeded,
        isFieldReselectPending: !!pendingReselectRecalcFieldRef.current,
        isFieldImportLanding: fieldImportAlreadyPersistedRef.current,
        wasImportReadyForRecalc: transitionImportReadyForRecalc,
        isAwaitingProjectChoice: isAwaitingProjectChoice && importedFieldCropYearsRef.current !== null,
        isAddToExistingFlow: addToExistingFlowRef.current,
      });
      if (action === "wait") return;
      // One shot per project load: later live edits never re-trigger it; readiness reached afterward comes from an explicit action.
      postHydrationLatch.markDecided();

      const pruneThenRecalculate = async (onPruned?: () => void) => {
        const pruned = await pruneAndPersistEmptyYears(scenarios);
        // Not aborted on re-entry: the prune's own re-render re-runs this effect, and the latch above makes that run bail out.
        if (!pruned) {
          // No run follows a failed prune (toast shown), so the "awaiting first calculation" loader must not stay up.
          setAwaitingInitialCalc(false);
          return;
        }
        onPruned?.();
        recalculate().catch(() => {});
      };

      switch (action) {
        case "landingRecalc":
          pruneThenRecalculate(() => {
            markLandingRecalcPending();
            setAwaitingInitialCalc(true);
          });
          break;
        case "landingIncomplete":
          setIsDirty(true);
          setAwaitingInitialCalc(false);
          // The transition modal is not shown in the add-to-existing path, so the landing scroll won't reveal the card.
          revealFirstIncompleteScenario();
          break;
        case "reviewAddedRotation":
          addToExistingFlowRef.current = false;
          setIsDirty(true);
          setAwaitingInitialCalc(false);
          break;
        case "recalc":
          // Background auto-recalc, not a user click — leave the viewport wherever the user is.
          pruneThenRecalculate();
          break;
        case "showIncomplete":
          setIsDirty(true);
          setAwaitingInitialCalc(false);
          flagIncompleteScenarios();
          break;
        case "stayQuiet":
          break;
        default: {
          const unhandledAction: never = action;
          throw new Error(`Unhandled post-hydration action: ${unhandledAction}`);
        }
      }
    },
    [
      addToExistingFlowRef,
      cropYearsSavedRef,
      farmFieldsFetching,
      fieldImportAlreadyPersistedRef,
      fieldReselectNeeded,
      flagIncompleteScenarios,
      hydrationApplied,
      importedFieldCropYearsRef,
      isAwaitingProjectChoice,
      isSavingCount,
      markLandingRecalcPending,
      pendingReselectRecalcFieldRef,
      postHydrationLatch,
      pruneAndPersistEmptyYears,
      recalculate,
      revealFirstIncompleteScenario,
      scenarios,
      setAwaitingInitialCalc,
      setIsDirty,
      soilSightProjectId,
      transitionImportReadyForRecalc,
    ]
  );
};
